<script setup lang="ts">
import type { ImportJob } from '../internal/types.ts'
import { formatInstantDate } from '@taler/domain'
import { EmptyState, ErrorState, StatusTag } from '@taler/ui'
import { useI18n } from 'vue-i18n'

defineProps<{
  jobs: readonly ImportJob[]
  isLoading: boolean
  error: Error | null
  retrying: boolean
}>()

const emit = defineEmits<{ retry: [] }>()

const { t, locale } = useI18n()
</script>

<template>
  <div>
    <p v-if="isLoading" role="status" class="text-[color:var(--color-text-muted)]">
      {{ t('imports.list.loading') }}
    </p>

    <ErrorState
      v-else-if="error"
      :title="t('imports.list.errorTitle')"
      :body="t('imports.list.errorBody')"
      :retry-label="t('imports.list.retry')"
      :retrying="retrying"
      @retry="emit('retry')"
    />

    <EmptyState
      v-else-if="jobs.length === 0"
      :title="t('imports.list.emptyTitle')"
      :body="t('imports.list.emptyBody')"
    />

    <div
      v-else
      class="overflow-x-auto"
      tabindex="0"
      role="region"
      :aria-label="t('imports.list.tableRegion')"
    >
      <table class="w-full border-collapse bg-[color:var(--color-surface)]">
        <thead>
          <tr class="table-head">
            <th class="px-[var(--space-cell)] py-[var(--space-inline)] font-semibold" scope="col">
              {{ t('imports.list.uploaded') }}
            </th>
            <th class="px-[var(--space-cell)] py-[var(--space-inline)] font-semibold" scope="col">
              {{ t('imports.list.file') }}
            </th>
            <th class="px-[var(--space-cell)] py-[var(--space-inline)] font-semibold" scope="col">
              {{ t('imports.list.statusLabel') }}
            </th>
            <th class="px-[var(--space-cell)] py-[var(--space-inline)] font-semibold" scope="col">
              <span class="sr-only">{{ t('imports.list.actions') }}</span>
            </th>
          </tr>
        </thead>
        <tbody>
          <tr v-for="job in jobs" :key="job.id" class="border-b border-[color:var(--color-border)]">
            <td
              class="whitespace-nowrap px-[var(--space-cell)] py-[var(--space-cell)] text-[color:var(--color-text)]"
            >
              {{ formatInstantDate(job.createdAt, locale as 'en' | 'hu') }}
            </td>
            <td class="px-[var(--space-cell)] py-[var(--space-cell)] text-[color:var(--color-text)]">
              {{ job.filename }}
            </td>
            <td class="px-[var(--space-cell)] py-[var(--space-cell)]">
              <StatusTag v-if="job.status === 'COMMITTED'" tone="ok">
                {{ t('imports.status.COMMITTED') }}
              </StatusTag>
              <StatusTag v-else-if="job.status === 'ABANDONED'" tone="neutral">
                {{ t('imports.status.ABANDONED') }}
              </StatusTag>
              <StatusTag v-else tone="neutral">{{ t('imports.status.OPEN') }}</StatusTag>
            </td>
            <td class="px-[var(--space-cell)] py-[var(--space-cell)] text-right">
              <RouterLink
                :to="{ name: 'import-job-detail', params: { jobId: job.id } }"
                class="min-h-[var(--density-target-min)] rounded-[var(--radius-control)] border border-[color:var(--color-border-control)] px-3 py-2 text-[color:var(--color-text)]"
              >
                {{ job.status === 'OPEN' ? t('imports.list.continue') : t('imports.list.view') }}
              </RouterLink>
            </td>
          </tr>
        </tbody>
      </table>
    </div>
  </div>
</template>
